/**
 * CTRL_08 Dashboard - Human Oversight & Review
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, Empty, Progress } from 'antd';
import {
  CheckCircleOutlined,
  UserOutlined,
  ClockCircleOutlined,
  SwapOutlined
} from '@ant-design/icons';
import { getControlResults, getControlEvents } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

function CTRL_08_Dashboard({ projectId }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [events, setEvents] = useState([]);
  const controlCode = 'CTRL_08';

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resultsRes, eventsRes] = await Promise.all([
        getControlResults(projectId),
        getControlEvents(projectId, controlCode).catch(() => ({ data: { events: [] } }))
      ]);

      const controlResults = (resultsRes.data.results || []).find(r => r.control_code === controlCode);
      setResults(controlResults);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate review coverage
  const reviewCoverage = useMemo(() => {
    if (events.length === 0) return { total: 0, reviewed: 0, percentage: 0 };

    const reviewed = events.filter(e => {
      const metadata = e.event_metadata || {};
      return metadata.human_reviewer || metadata.human_decision;
    }).length;

    return {
      total: events.length,
      reviewed,
      percentage: Math.round((reviewed / events.length) * 100)
    };
  }, [events]);

  // Calculate override rate
  const overrideRate = useMemo(() => {
    const reviewedEvents = events.filter(e => {
      const metadata = e.event_metadata || {};
      return metadata.human_decision && metadata.ai_recommendation;
    });

    if (reviewedEvents.length === 0) return { total: 0, overrides: 0, percentage: 0 };

    const overrides = reviewedEvents.filter(e => {
      const metadata = e.event_metadata;
      return metadata.human_decision !== metadata.ai_recommendation;
    }).length;

    return {
      total: reviewedEvents.length,
      overrides,
      percentage: Math.round((overrides / reviewedEvents.length) * 100)
    };
  }, [events]);

  // Calculate reviewer performance
  const reviewerPerformance = useMemo(() => {
    const reviewsByReviewer = {};

    events.forEach(e => {
      const reviewer = e.event_metadata?.human_reviewer;
      if (!reviewer) return;

      if (!reviewsByReviewer[reviewer]) {
        reviewsByReviewer[reviewer] = {
          count: 0,
          totalDuration: 0,
          overrides: 0
        };
      }

      reviewsByReviewer[reviewer].count++;

      const duration = e.event_metadata?.review_duration_minutes;
      if (duration) {
        reviewsByReviewer[reviewer].totalDuration += duration;
      }

      if (e.event_metadata?.human_decision !== e.event_metadata?.ai_recommendation) {
        reviewsByReviewer[reviewer].overrides++;
      }
    });

    return Object.entries(reviewsByReviewer).map(([reviewer, stats]) => ({
      reviewer,
      reviewCount: stats.count,
      avgDuration: stats.totalDuration > 0 ? (stats.totalDuration / stats.count).toFixed(1) : 0,
      overrideRate: Math.round((stats.overrides / stats.count) * 100)
    }));
  }, [events]);

  // Calculate average review latency
  const avgReviewLatency = useMemo(() => {
    const durations = events
      .map(e => e.event_metadata?.review_duration_minutes)
      .filter(d => d !== undefined && d !== null);

    if (durations.length === 0) return 0;

    const sum = durations.reduce((acc, d) => acc + d, 0);
    return (sum / durations.length).toFixed(1);
  }, [events]);

  // Calculate override reasons breakdown
  const overrideReasons = useMemo(() => {
    const reasonCounts = {};

    events.forEach(e => {
      const reason = e.event_metadata?.override_reason;
      if (reason) {
        reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
      }
    });

    return Object.entries(reasonCounts).map(([reason, count]) => ({
      reason,
      count,
      percentage: Math.round((count / Object.values(reasonCounts).reduce((sum, c) => sum + c, 0)) * 100)
    }));
  }, [events]);

  // Table columns for recent reviews
  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'ts',
      key: 'ts',
      render: (ts) => new Date(ts).toLocaleString(),
      width: 180
    },
    {
      title: 'Reviewer',
      dataIndex: ['event_metadata', 'human_reviewer'],
      key: 'reviewer',
      ellipsis: true,
      render: (reviewer) => reviewer || '-'
    },
    {
      title: 'AI Recommendation',
      dataIndex: ['event_metadata', 'ai_recommendation'],
      key: 'ai_rec',
      render: (rec) => rec ? <Tag color="blue">{rec}</Tag> : '-'
    },
    {
      title: 'Human Decision',
      dataIndex: ['event_metadata', 'human_decision'],
      key: 'human_dec',
      render: (dec) => dec ? <Tag color="green">{dec}</Tag> : '-'
    },
    {
      title: 'Override',
      key: 'override',
      align: 'center',
      render: (_, record) => {
        const aiRec = record.event_metadata?.ai_recommendation;
        const humanDec = record.event_metadata?.human_decision;
        const isOverride = aiRec && humanDec && aiRec !== humanDec;

        return isOverride ?
          <Tag color="orange" icon={<SwapOutlined />}>Yes</Tag> :
          <Tag color="green">No</Tag>;
      }
    },
    {
      title: 'Override Reason',
      dataIndex: ['event_metadata', 'override_reason'],
      key: 'reason',
      ellipsis: true,
      render: (reason) => reason || '-'
    },
    {
      title: 'Duration',
      dataIndex: ['event_metadata', 'review_duration_minutes'],
      key: 'duration',
      render: (duration) => duration ? `${duration.toFixed(1)} min` : '-'
    }
  ];

  // Reviewer performance table columns
  const reviewerColumns = [
    {
      title: 'Reviewer',
      dataIndex: 'reviewer',
      key: 'reviewer'
    },
    {
      title: 'Reviews',
      dataIndex: 'reviewCount',
      key: 'reviewCount',
      align: 'center'
    },
    {
      title: 'Avg Duration',
      dataIndex: 'avgDuration',
      key: 'avgDuration',
      align: 'center',
      render: (duration) => `${duration} min`
    },
    {
      title: 'Override Rate',
      dataIndex: 'overrideRate',
      key: 'overrideRate',
      align: 'center',
      render: (rate) => (
        <div style={{ width: '100px' }}>
          <Progress
            percent={rate}
            size="small"
            strokeColor={rate > 50 ? '#fa8c16' : '#1890ff'}
          />
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!projectId) {
    return (
      <Empty
        description="Please select a project to view dashboard"
        style={{ padding: '40px' }}
      />
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>CTRL_08: Human Oversight & Review Dashboard</h2>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Review Coverage"
              value={reviewCoverage.percentage}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: reviewCoverage.percentage >= 80 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {reviewCoverage.reviewed} of {reviewCoverage.total} decisions reviewed
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Override Rate"
              value={overrideRate.percentage}
              suffix="%"
              prefix={<SwapOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {overrideRate.overrides} of {overrideRate.total} recommendations overridden
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avg Review Time"
              value={avgReviewLatency}
              suffix="min"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: avgReviewLatency < 10 ? '#52c41a' : '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Reviewers"
              value={reviewerPerformance.length}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Reviewer Performance Table */}
      <Card title="Reviewer Performance" style={{ marginBottom: '24px' }}>
        <Table
          dataSource={reviewerPerformance}
          columns={reviewerColumns}
          rowKey="reviewer"
          pagination={false}
          size="small"
        />
      </Card>

      {/* Override Reasons Breakdown */}
      {overrideReasons.length > 0 && (
        <Card title="Override Reasons Breakdown" style={{ marginBottom: '24px' }}>
          <Row gutter={16}>
            {overrideReasons.map((reason, idx) => (
              <Col xs={24} sm={12} key={idx} style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontWeight: 'bold' }}>{reason.reason}</span>
                  <span style={{ color: '#666' }}>{reason.count} ({reason.percentage}%)</span>
                </div>
                <Progress
                  percent={reason.percentage}
                  strokeColor="#1890ff"
                  size="small"
                />
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* Recent Reviews Table */}
      <Card title="Recent Reviews">
        <Table
          dataSource={events.slice(0, 20)}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} reviews`
          }}
          size="small"
        />
      </Card>
    </div>
  );
}

export default CTRL_08_Dashboard;
